import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  UploadCloud,
  X,
  Loader2,
} from 'lucide-react';
import { logger } from '@client/src/utils/logger';

import { orders } from '@client/src/api';
import { Button } from '@client/src/components/ui/button';
import { Input } from '@client/src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@client/src/components/ui/select';
import { Image } from '@client/src/components/ui/image';
import { type OrderPlacer, type Platform, type ShippingStatus, type WarehouseStatus } from '@client/src/types/order';
import { getOrderPlacers, getPlatformCountries } from '@client/src/config/env';

type LocationState = { platform?: Platform };

const OrderNewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const platform: Platform = state?.platform ?? 'depop';

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imageUrl, setImageUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [country, setCountry] = useState<string>('');
  const [customerName, setCustomerName] = useState('');
  const [orderPlacer, setOrderPlacer] = useState<string>('');
  const [orderDate, setOrderDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [shippingStatus, setShippingStatus] = useState<ShippingStatus>('unshipped');
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [warehouseStatus, setWarehouseStatus] = useState<WarehouseStatus>('not_arrived');
  const [isRefunded, setIsRefunded] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);

  const countries = getPlatformCountries(platform);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await orders.uploadImage(formData);

      if (response.data?.url) {
        setImageUrl(response.data.url);
      } else {
        toast.error('图片上传失败');
      }
    } catch (err) {
      logger.error('图片上传异常', err);
      toast.error('图片上传失败');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setImageUrl('');
  };

  const validate = (): string | null => {
    if (!imageUrl) return '请上传订单图片';
    if (!country) return '请选择国家';
    if (shippingStatus === 'shipped' && !trackingNumber.trim()) {
      return '请输入运单号';
    }
    return null;
  };

  const handleSubmit = async () => {
    const errorMsg = validate();
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }

    setSubmitting(true);
    try {
      const payload: {
        platform: Platform;
        country: string;
        customerName?: string;
        orderPlacer?: OrderPlacer;
        orderDate?: string;
        shippingStatus: ShippingStatus;
        trackingNumber?: string;
        warehouseStatus: WarehouseStatus;
        isRefunded: boolean;
        imageUrl: string;
      } = {
        platform,
        country,
        customerName: customerName.trim() || undefined,
        orderPlacer: (orderPlacer as OrderPlacer) || undefined,
        orderDate: orderDate || undefined,
        shippingStatus,
        warehouseStatus,
        isRefunded,
        imageUrl,
      };

      if (shippingStatus === 'shipped') {
        payload.trackingNumber = trackingNumber.trim();
      }

      await orders.createOrder(payload);
      toast.success('创建成功');
      navigate(-1);
    } catch (err) {
      logger.error('创建订单失败', err);
      toast.error('创建失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const PillButton = ({
    active,
    onClick,
    children,
  }: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex-1 h-10 rounded-full text-sm font-medium transition-colors ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : 'bg-accent text-accent-foreground')
      }
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* 顶部导航栏 */}
      <header className="sticky top-0 z-10 bg-card border-b border-border h-[52px] flex items-center justify-between px-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center -ml-2 text-foreground"
          aria-label="返回"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-medium text-foreground">新增订单</h1>
        <Button
          variant="default"
          size="sm"
          onClick={handleSubmit}
          disabled={submitting || uploading}
          className="min-w-[60px]"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            '提交'
          )}
        </Button>
      </header>

      {/* 表单内容区 */}
      <main className="flex-1 pt-4 pb-8 px-4 space-y-6 max-w-md mx-auto w-full">
        {/* 图片上传区 */}
        <section>
          <label className="text-sm font-medium text-foreground mb-2 block">
            订单图片
          </label>
          <div className="w-full aspect-square">
            {imageUrl ? (
              <div className="relative w-full h-full rounded-lg overflow-hidden">
                <Image
                  src={imageUrl}
                  alt="订单图片"
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center"
                  aria-label="删除图片"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={uploading}
                className="w-full h-full border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center text-muted-foreground gap-2 bg-card hover:border-primary/50 transition-colors"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-8 h-8 animate-spin" />
                    <span className="text-sm">上传中...</span>
                  </>
                ) : (
                  <>
                    <UploadCloud className="w-8 h-8" />
                    <span className="text-sm">点击上传图片</span>
                  </>
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="sr-only"
            />
          </div>
        </section>

        {/* 国家选择 */}
        <section>
          <label className="text-sm font-medium text-foreground mb-2 block">
            国家<span className="text-destructive">*</span>
          </label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="请选择国家" />
            </SelectTrigger>
            <SelectContent>
              {countries.map((c: string) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </section>

        {/* 客户名字 */}
        <section>
          <label className="text-sm font-medium text-foreground mb-2 block">
            客户名字
          </label>
          <Input
            type="text"
            placeholder="请输入客户名字"
            value={customerName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCustomerName(e.target.value)
            }
          />
        </section>

        {/* 下单人 */}
        <section>
          <label className="text-sm font-medium text-foreground mb-2 block">
            下单人
          </label>
          <Select value={orderPlacer} onValueChange={setOrderPlacer}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="请选择下单人" />
            </SelectTrigger>
            <SelectContent>
              {getOrderPlacers(platform).map((p: string) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </section>

        {/* 下单日期 */}
        <section>
          <label className="text-sm font-medium text-foreground mb-2 block">
            下单日期
          </label>
          <Input
            type="date"
            value={orderDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOrderDate(e.target.value)
            }
          />
        </section>

        {/* 发货状态 */}
        <section>
          <label className="text-sm font-medium text-foreground mb-2 block">
            发货状态<span className="text-destructive">*</span>
          </label>
          <div className="flex gap-3">
            <PillButton
              active={shippingStatus === 'unshipped'}
              onClick={() => setShippingStatus('unshipped')}
            >
              未发
            </PillButton>
            <PillButton
              active={shippingStatus === 'shipped'}
              onClick={() => setShippingStatus('shipped')}
            >
              已发
            </PillButton>
          </div>
        </section>

        {/* 运单号（仅已发时显示） */}
        {shippingStatus === 'shipped' && (
          <section className="animate-in fade-in duration-200">
            <label className="text-sm font-medium text-foreground mb-2 block">
              运单号<span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              placeholder="请输入运单号"
              value={trackingNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTrackingNumber(e.target.value)
              }
            />
          </section>
        )}

        {/* 到库状态 */}
        <section>
          <label className="text-sm font-medium text-foreground mb-2 block">
            到库状态<span className="text-destructive">*</span>
          </label>
          <div className="flex gap-3">
            <PillButton
              active={warehouseStatus === 'arrived'}
              onClick={() => setWarehouseStatus('arrived')}
            >
              已到
            </PillButton>
            <PillButton
              active={warehouseStatus === 'not_arrived'}
              onClick={() => setWarehouseStatus('not_arrived')}
            >
              未到
            </PillButton>
          </div>
        </section>

        {/* 是否退款 */}
        <section>
          <label className="text-sm font-medium text-foreground mb-2 block">
            是否退款<span className="text-destructive">*</span>
          </label>
          <div className="flex gap-3">
            <PillButton
              active={isRefunded}
              onClick={() => setIsRefunded(true)}
            >
              是
            </PillButton>
            <PillButton
              active={!isRefunded}
              onClick={() => setIsRefunded(false)}
            >
              否
            </PillButton>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OrderNewPage;
